import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { publicRoutes } from '@/shared/constants/publicRoutes'
import { authTokenStorage, StoredAuthTokens } from '@shared/lib/authTokenStorage'
import { apiService } from '@shared/api/auth-instance'
import { KcProxy } from '@/shared/api/kcProxy'
import { CustomLoader } from '@/shared/animations/loaders/CustomLoader'
import { ROUTES } from '@shared/constants/routes'
import { useIonRouter } from '@ionic/react'
import {
	clearPendingAuthRequest,
	getPendingAuthRequest,
	storeProgramAuthReturnPath,
} from '@shared/lib/authRedirect'
import { toast } from 'sonner'

export type AuthMode = 'public' | 'keycloak' | 'token'
export type AuthStatus =
	| 'public'
	| 'bootstrapping'
	| 'refreshing'
	| 'authenticated'
	| 'unauthenticated'

export type AuthModeState = {
	mode: AuthMode
	status: AuthStatus
	initialized: boolean
	authenticated: boolean
	accessToken: string | null
	refreshToken: string | null
	ensureFreshSession: () => Promise<string | null>
	logout: () => Promise<void>
}

type RefreshSessionResult =
	| { status: 'refreshed'; tokens: StoredAuthTokens }
	| { status: 'failed' }
	| { status: 'stale' }

const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Войдите снова.'
const AUTH_ROUTE_FALLBACK_DELAY_MS = 500

const AuthModeContext = createContext<AuthModeState>({
	mode: 'public',
	status: 'public',
	initialized: false,
	authenticated: false,
	accessToken: null,
	refreshToken: null,
	ensureFreshSession: async () => null,
	logout: async () => undefined,
})

export const useAuthMode = (): AuthModeState => useContext(AuthModeContext)

export const AuthModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const location = useLocation()
	const nav = useIonRouter()
	const isPublicRoute = publicRoutes.includes(location.pathname)
	const [storedTokens, setStoredTokens] = useState<StoredAuthTokens | null | undefined>(undefined)
	const [refreshStatus, setRefreshStatus] = useState<'idle' | 'refreshing' | 'failed'>('idle')
	const [callbackStatus, setCallbackStatus] = useState<'idle' | 'handling' | 'done'>('idle')
	const latestStoredTokensRef = useRef<StoredAuthTokens | null | undefined>(storedTokens)
	const refreshPromiseRef = useRef<Promise<RefreshSessionResult> | null>(null)
	const authEpochRef = useRef(0)
	const sessionFailureNotifiedRef = useRef(false)
	const isAuthCallbackRoute = location.pathname === ROUTES.auth.enterBaseInfo
	const [callbackHash, setCallbackHash] = useState<string>(() =>
		typeof window === 'undefined' ? '' : window.location.hash
	)
	const callbackHashParams = useMemo(
		() => new URLSearchParams(callbackHash.startsWith('#') ? callbackHash.slice(1) : ''),
		[callbackHash]
	)
	const hasAuthCallbackParams = useMemo(
		() =>
			isAuthCallbackRoute &&
			(callbackHashParams.has('code') ||
				callbackHashParams.has('error') ||
				callbackHashParams.has('state')),
		[callbackHashParams, isAuthCallbackRoute]
	)
	const isProcessingAuthCallback = hasAuthCallbackParams || callbackStatus === 'handling'

	const updateStoredTokens = useCallback((tokens: StoredAuthTokens | null | undefined): void => {
		latestStoredTokensRef.current = tokens
		setStoredTokens(tokens)
	}, [])

	const clearPersistedTokens = useCallback(async (): Promise<void> => {
		authEpochRef.current += 1
		try {
			await authTokenStorage.clearTokens()
		} catch (error) {
			console.error('Failed to clear auth tokens:', error)
		}
	}, [])

	const pushAuthEntryRoute = useCallback((): void => {
		nav.push(ROUTES.overall.hello)
		window.setTimeout(() => {
			if (window.location.pathname !== ROUTES.overall.hello) {
				window.location.replace(ROUTES.overall.hello)
			}
		}, AUTH_ROUTE_FALLBACK_DELAY_MS)
	}, [nav])

	const persistAuthenticatedTokens = useCallback(
		async (
			tokens: {
				accessToken: string
				refreshToken: string | null
			},
			generation?: string
		): Promise<StoredAuthTokens> => {
			const nextStoredTokens = {
				...tokens,
				storedAt: Date.now(),
			}

			await authTokenStorage.setTokens(
				nextStoredTokens.accessToken,
				nextStoredTokens.refreshToken,
				generation
			)
			return nextStoredTokens
		},
		[]
	)

	const finishUnauthenticatedSession = useCallback(
		async (showExpiredMessage: boolean): Promise<void> => {
			storeProgramAuthReturnPath(`${location.pathname}${location.search}${location.hash}`)
			setRefreshStatus('failed')
			updateStoredTokens(null)
			clearPendingAuthRequest()
			await clearPersistedTokens()

			if (showExpiredMessage && !sessionFailureNotifiedRef.current) {
				sessionFailureNotifiedRef.current = true
				toast.error(SESSION_EXPIRED_MESSAGE)
			}

			pushAuthEntryRoute()
		},
		[
			clearPersistedTokens,
			location.hash,
			location.pathname,
			location.search,
			pushAuthEntryRoute,
			updateStoredTokens,
		]
	)

	const logout = useCallback(async (): Promise<void> => {
		sessionFailureNotifiedRef.current = false
		await finishUnauthenticatedSession(false)
	}, [finishUnauthenticatedSession])

	const ensureFreshSession = useCallback(async (): Promise<string | null> => {
		let tokens = latestStoredTokensRef.current

		if (tokens === undefined) {
			try {
				tokens = await authTokenStorage.getTokens()
				updateStoredTokens(tokens)
			} catch {
				tokens = null
				updateStoredTokens(null)
			}
		}

		if (
			tokens?.accessToken &&
			!authTokenStorage.isAccessTokenExpired(tokens.accessToken)
		) {
			return tokens.accessToken
		}

		const refreshToken = tokens?.refreshToken ?? null
		if (!refreshToken || authTokenStorage.isRefreshTokenExpired(refreshToken)) {
			await finishUnauthenticatedSession(Boolean(tokens?.accessToken || tokens?.refreshToken))
			return null
		}

		if (!refreshPromiseRef.current) {
			setRefreshStatus('refreshing')
			const refreshEpoch = authEpochRef.current
			const refreshGeneration = authTokenStorage.getCurrentGeneration()
			refreshPromiseRef.current = (async (): Promise<RefreshSessionResult> => {
				const discardStaleRefresh = async (clearPersistedGeneration: boolean) => {
					setRefreshStatus('idle')
					if (clearPersistedGeneration) {
						await authTokenStorage.clearTokensForGeneration(refreshGeneration)
					}
					return { status: 'stale' } as const
				}

				const nextTokens = await KcProxy.refreshTokens(refreshToken)
				if (!nextTokens?.accessToken) return { status: 'failed' }

				const latestTokens = latestStoredTokensRef.current
				if (
					authEpochRef.current !== refreshEpoch ||
					latestTokens?.refreshToken !== refreshToken
				) {
					return discardStaleRefresh(false)
				}

				const refreshedTokens = await persistAuthenticatedTokens(
					{
						accessToken: nextTokens.accessToken,
						refreshToken: nextTokens.refreshToken ?? refreshToken,
					},
					refreshGeneration
				)
				const currentTokens = latestStoredTokensRef.current
				if (
					authEpochRef.current !== refreshEpoch ||
					currentTokens?.refreshToken !== refreshToken
				) {
					return discardStaleRefresh(true)
				}
				sessionFailureNotifiedRef.current = false
				updateStoredTokens(refreshedTokens)
				setRefreshStatus('idle')
				return { status: 'refreshed', tokens: refreshedTokens }
			})()
				.catch(error => {
					console.error('Failed to refresh auth session:', error)
					return { status: 'failed' }
				})
				.finally(() => {
					refreshPromiseRef.current = null
				})
		}

		const refreshResult = await refreshPromiseRef.current
		if (refreshResult.status === 'stale') {
			const latestTokens = latestStoredTokensRef.current
			if (
				latestTokens?.accessToken &&
				!authTokenStorage.isAccessTokenExpired(latestTokens.accessToken)
			) {
				return latestTokens.accessToken
			}
			return null
		}

		if (refreshResult.status === 'failed') {
			await finishUnauthenticatedSession(true)
			return null
		}

		return refreshResult.tokens.accessToken
	}, [finishUnauthenticatedSession, persistAuthenticatedTokens, updateStoredTokens])

	useEffect(() => {
		if (typeof window === 'undefined') return
		setCallbackHash(window.location.hash)
	}, [location.pathname])

	useEffect(() => {
		if (!hasAuthCallbackParams) {
			setCallbackStatus('idle')
			return
		}

		let cancelled = false
		setCallbackStatus('handling')
		;(async () => {
			const callbackError = callbackHashParams.get('error')
			if (callbackError) {
				await clearPersistedTokens()
				clearPendingAuthRequest()
				if (cancelled) return
				updateStoredTokens(null)
				setCallbackHash('')
				setCallbackStatus('done')
				window.history.replaceState(null, '', location.pathname)
				pushAuthEntryRoute()
				return
			}

			try {
				const code = callbackHashParams.get('code')
				const state = callbackHashParams.get('state')
				const pendingAuthRequest = getPendingAuthRequest()

				if (!code || !state || !pendingAuthRequest || pendingAuthRequest.state !== state) {
					await clearPersistedTokens()
					clearPendingAuthRequest()
					if (cancelled) return
					updateStoredTokens(null)
					setCallbackHash('')
					setCallbackStatus('done')
					window.history.replaceState(null, '', location.pathname)
					pushAuthEntryRoute()
					return
				}

				const tokens = await KcProxy.exchangeAuthorizationCode({
					code,
					codeVerifier: pendingAuthRequest.codeVerifier,
					redirectUri: pendingAuthRequest.redirectUri,
				})

				if (!tokens?.accessToken) {
					await clearPersistedTokens()
					clearPendingAuthRequest()
					if (cancelled) return
					updateStoredTokens(null)
					setCallbackHash('')
					setCallbackStatus('done')
					window.history.replaceState(null, '', location.pathname)
					pushAuthEntryRoute()
					return
				}

				clearPendingAuthRequest()
				if (cancelled) return
				await clearPersistedTokens()
				if (cancelled) return
				const nextStoredTokens = await persistAuthenticatedTokens({
					accessToken: tokens.accessToken,
					refreshToken: tokens.refreshToken ?? null,
				})
				if (cancelled) return
				updateStoredTokens(nextStoredTokens)
				setRefreshStatus('idle')
				sessionFailureNotifiedRef.current = false
				setCallbackHash('')
				window.history.replaceState(null, '', location.pathname)
				setCallbackStatus('done')
			} catch {
				await clearPersistedTokens()
				clearPendingAuthRequest()
				if (cancelled) return
				updateStoredTokens(null)
				setCallbackHash('')
				setCallbackStatus('done')
				window.history.replaceState(null, '', location.pathname)
				pushAuthEntryRoute()
			}
		})()

		return () => {
			cancelled = true
		}
	}, [
		callbackHashParams,
		clearPersistedTokens,
		hasAuthCallbackParams,
		location.pathname,
		persistAuthenticatedTokens,
		pushAuthEntryRoute,
		updateStoredTokens,
	])

	useEffect(() => {
		if (isProcessingAuthCallback) return
		if (
			storedTokens?.accessToken &&
			!authTokenStorage.isAccessTokenExpired(storedTokens.accessToken)
		) {
			return
		}

		let cancelled = false
		;(async () => {
			try {
				const tokens = await authTokenStorage.getTokens()
				if (!cancelled) updateStoredTokens(tokens)
			} catch {
				if (!cancelled) updateStoredTokens(null)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [isProcessingAuthCallback, location.pathname, storedTokens?.accessToken, updateStoredTokens])

	const storedAccessToken = storedTokens?.accessToken ?? null
	const storedRefreshToken = storedTokens?.refreshToken ?? null
	const hasValidStoredAccessToken = Boolean(
		storedAccessToken && !authTokenStorage.isAccessTokenExpired(storedAccessToken)
	)
	const hasValidStoredRefreshToken = Boolean(
		storedRefreshToken && !authTokenStorage.isRefreshTokenExpired(storedRefreshToken)
	)

	useEffect(() => {
		if (isPublicRoute || isProcessingAuthCallback) return
		if (storedTokens === undefined) return
		if (
			!hasValidStoredAccessToken &&
			!hasValidStoredRefreshToken &&
			(storedAccessToken || storedRefreshToken)
		) {
			void finishUnauthenticatedSession(true)
		}
	}, [
		finishUnauthenticatedSession,
		hasValidStoredAccessToken,
		hasValidStoredRefreshToken,
		isPublicRoute,
		storedAccessToken,
		storedRefreshToken,
		storedTokens,
	])

	useEffect(() => {
		if (isPublicRoute || isProcessingAuthCallback) return
		if (storedTokens === undefined) return
		if (hasValidStoredAccessToken) return
		if (refreshStatus === 'refreshing') return

		if (!hasValidStoredRefreshToken || !storedRefreshToken) {
			void finishUnauthenticatedSession(Boolean(storedAccessToken || storedRefreshToken))
			return
		}

		void ensureFreshSession()
	}, [
		ensureFreshSession,
		finishUnauthenticatedSession,
		hasValidStoredAccessToken,
		hasValidStoredRefreshToken,
		isPublicRoute,
		isProcessingAuthCallback,
		refreshStatus,
		storedAccessToken,
		storedRefreshToken,
		storedTokens,
	])

	// Public routes render without auth initialization.
	if (isPublicRoute) {
		return (
			<AuthModeContext.Provider
				value={{
					mode: 'public',
					status: 'public',
					initialized: true,
					authenticated: false,
					accessToken: null,
					refreshToken: null,
					ensureFreshSession,
					logout,
				}}>
				{children}
			</AuthModeContext.Provider>
		)
	}

	if (isProcessingAuthCallback) {
		return <CustomLoader />
	}

	// Load secure storage before deciding between refresh vs Keycloak redirects.
	if (storedTokens === undefined) {
		return <CustomLoader />
	}

	// If access is missing/expired but refresh is valid, refresh via token endpoint (no redirects).
	if (!hasValidStoredAccessToken && hasValidStoredRefreshToken) {
		return <CustomLoader />
	}

	if (!hasValidStoredAccessToken) {
		return <CustomLoader />
	}

	// Ensure authAxios is ready before authenticated API queries mount.
	if (!storedAccessToken) {
		return <CustomLoader />
	}
	apiService.setupToken(storedAccessToken, ensureFreshSession)

	return (
		<AuthModeContext.Provider
			value={{
				mode: 'token',
				status: 'authenticated',
				initialized: true,
				authenticated: true,
				accessToken: storedAccessToken,
				refreshToken: storedRefreshToken,
				ensureFreshSession,
				logout,
			}}>
			{children}
		</AuthModeContext.Provider>
	)
}
